import clsx from "clsx";
import {
  type ComponentProps,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Autoplay, EffectFade, FreeMode } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperCore } from "swiper/types";
import Typed from "typed.js";

import { PaginationCards } from "@features/home-hero-pagination";

import type { ColorIconType } from "@shared/content-types/types";
import { ButtonWithIcon } from "@shared/ui/button-with-icon";
import { Link } from "@shared/ui/link";
import { LoadingSpinner } from "@shared/ui/loading-spinner";
import { MediaBackground } from "@shared/ui/media-background";
import { MessageWrapper } from "@shared/ui/message-wrapper";
import { SwiperNavButtons } from "@shared/ui/slider-nav-buttons";
import { Body, Heading } from "@shared/ui/typography";
import {
  ApplyReplacements,
  mapItemsToPositionedIcons,
} from "@shared/ui/typography/apply-replacements-to-text";

import type { HomeHeroType } from "@/_pages/home/model";

import s from "./home-hero.module.scss";

export type HomeHeroProps = ComponentProps<"div"> & {
  className?: string;
  slides: HomeHeroType[];
  pageIcon: ColorIconType;
  AITexts: { text: string }[];
};

const SWIPER_CONFIG = {
  AUTOPLAY_DELAY: 5000,
};

export const HomeHero = (props: HomeHeroProps) => {
  const { className, slides, pageIcon, AITexts } = props;

  const $root = useRef<HTMLDivElement | null>(null);
  const $swiperRef = useRef<SwiperCore | null>(null);
  const paginationRef = useRef<SwiperCore | null>(null);
  const AITextRef = useRef(null);

  const [currentIndex, setCurrentIndex] = useState(0);

  const handleClickOnPagination = useCallback((index: number) => {
    setCurrentIndex(index);
    $swiperRef?.current?.slideToLoop(index);
    paginationRef?.current?.slideToLoop(index);
  }, []);

  const handleSlideChange = useCallback((index: number) => {
    setCurrentIndex(index);
    paginationRef?.current?.slideToLoop(index);
  }, []);

  // const updateProgress = useCallback((time: number) => {
  //   const progress = round(1 - time / SWIPER_CONFIG.AUTOPLAY_DELAY, 3);

  //   paginationRef?.current?.style.setProperty(
  //     "--progress",
  //     progress.toString(),
  //   );
  // }, []);

  useEffect(() => {
    if (AITexts && AITexts.length > 0) {
      const typed = new Typed(AITextRef.current, {
        strings: AITexts.map((item) => item.text),
        typeSpeed: 50,
        backDelay: 4000,
        backSpeed: 30,
        loop: true,
      });

      return () => {
        typed.destroy();
      };
    }
  }, [AITexts]);

  /**
   * Раньше здесь через <link rel="preload" fetchpriority="high"> предзагружались
   * фоны всех слайдов, причём сырыми ссылками на Strapi — мимо imgproxy. Браузер
   * качал несколько мегабайт оригиналов, которые нигде не использовались (разметка
   * запрашивает варианты imgproxy), и они же конкурировали с настоящим LCP.
   * Приоритет теперь стоит на самой картинке первого слайда.
   */

  return (
    <div className={clsx(s.root, className)} ref={$root}>
      {AITexts && AITexts.length > 0 && (
        <div className={s.top}>
          <div className={s.subtitle}>
            <LoadingSpinner />
            <Body size="caption-small">Сейчас люди ищут</Body>
          </div>
          <div className={s.texts}>
            <MessageWrapper variant="left-bottom" className={s.text}>
              <Body
                tag="span"
                size="caption"
                weight="semiBold"
                ref={AITextRef}
              ></Body>
            </MessageWrapper>
          </div>
          <Link
            href="/ai-assistant"
            className={s.aiLink}
            aria-label="Перейти к AI-ассистенту"
          >
            &nbsp;
          </Link>
        </div>
      )}
      <Swiper
        modules={[Autoplay, EffectFade]}
        className={s.slider}
        loop
        effect="fade"
        fadeEffect={{ crossFade: true }}
        autoplay={{
          delay: SWIPER_CONFIG.AUTOPLAY_DELAY,
        }}
        onSlideChange={({ realIndex }) => handleSlideChange(realIndex)}
        onAutoplay={({ realIndex }) => handleSlideChange(realIndex)}
        onInit={(swiper) => {
          $swiperRef.current = swiper;
        }}
        // onAutoplayTimeLeft={(_, time) => updateProgress(time)}
      >
        {(slides ?? []).map((slide, index) => (
          <SwiperSlide
            key={slide?.id ?? `slide-${index + 1}`}
            className={s.slide}
          >
            {slide?.backgroundImage && (
              <MediaBackground
                media={slide.backgroundImage}
                mediaMobile={slide.backgroundImageMobile}
                className={s.slideImage}
                imageDarkening={slide.imageDarkening}
                priority={index === 0}
              />
            )}
            <div className={s.content}>
              {slide?.title?.text && (
                <Heading
                  level={1}
                  tag="h4"
                  className={s.title}
                  aria-label={slide.title.text}
                >
                  <ApplyReplacements
                    text={slide.title.text}
                    settings={mapItemsToPositionedIcons(
                      slide.title?.titleIconSettings ?? [],
                    )}
                  />
                </Heading>
              )}
              {slide?.buttonText && slide?.link && (
                <ButtonWithIcon
                  href={slide.link}
                  type={undefined}
                  iconFillColor={pageIcon?.fillColor}
                  iconStrokeColor={pageIcon?.strokeColor}
                  iconName={pageIcon?.icon?.name ?? "plus"}
                  iconSvgCode={pageIcon?.uicon?.svg_code}
                  className={s.slideBtn}
                >
                  {slide.buttonText}
                </ButtonWithIcon>
              )}
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
      {slides?.length > 2 && (
        <SwiperNavButtons
          swiperRef={$swiperRef}
          variant="light"
          className={s.navigation}
        />
      )}
      {(slides?.length ?? 0) > 1 && (
        <div className={s.pagination}>
          <Swiper
            className={s.paginationSlider}
            wrapperClass={`${s.paginationWrapper}`}
            freeMode={true}
            modules={[FreeMode]}
            breakpoints={{
              200: {
                slidesPerView: "auto",
              },
              768: {
                slidesPerView: 7,
              },
              1024: {
                slidesPerView: slides?.length > 4 ? 4 : slides?.length,
              },
            }}
            onInit={(swiper) => {
              paginationRef.current = swiper;
            }}
          >
            {(slides ?? []).map((card, idx) =>
              card ? (
                <SwiperSlide
                  key={card?.id ?? `card_${idx}`}
                  className={s.paginationCard}
                >
                  <PaginationCards
                    card={card}
                    isActive={idx === currentIndex}
                    index={idx}
                    onPaginationClick={handleClickOnPagination}
                  />
                </SwiperSlide>
              ) : null,
            )}
          </Swiper>
        </div>
      )}
    </div>
  );
};

HomeHero.displayName = "HomeHero";
